import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { useDrag, useDrop } from 'react-dnd';

import boTheme from '@ecp/common/src/style/theme/bo';
import { textEllipsis } from '@ecp/common/src/style/theme/defaultStyled';
import { FlexBox, FlexBoxCenter } from '@ecp/common/src/layouts/flex/styled';
import { Spacing } from '@ecp/common/src/layouts/Spacing';

import { withMenu } from '@bo-recoil/common/selector';
import { useBookmarkMenu } from '@bo-hooks/common/useBookmarkMenu';
import { ReactComponent as BookmarkSelectedIcon } from '@bo-assets/icon/common/icon__bookmark--selected.svg';
import { ReactComponent as BookmarkIcon } from '@bo-assets/icon/common/icon__bookmark.svg';
import { ReactComponent as BookmarkHandle } from '@bo-assets/icon/common/icon__lnb-handle.svg';

const Container = styled(FlexBox)`
  width: 100%;
  height: 42px;
  flex-shrink: 0;

  ${({ active }) => active && activeStyle}
  opacity: ${({ opacity }) => opacity};
`;

const activeStyle = css`
  & div {
    font-weight: ${boTheme.font.weight.bold};
  }

  background-color: ${boTheme.color.menu3};
`;

const LeafMenuName = styled.div`
  cursor: pointer;
  font-size: ${boTheme.font.size.small};
  font-weight: ${boTheme.font.weight.regular};
  color: ${boTheme.color.sub10};

  ${textEllipsis}
`;

const BookmarkIconWrapper = styled(FlexBoxCenter)`
  cursor: pointer;
  width: 24px;
  height: 22px;
`;

const BookmarkHandleWrapper = styled.div`
  position: absolute;
  top: 10px;
  left: 239px;

  cursor: grab;
`;

export const DND_ITEM_TYPE = 'bookmark-menu';

const LnbBookmarkMenu = ({ bookmarkMenuNumber, onMove, onDrop }) => {
  const bookmarkMenu = useRecoilValue(withMenu(bookmarkMenuNumber));
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { updateBookmarkMenu } = useBookmarkMenu(bookmarkMenuNumber);

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: DND_ITEM_TYPE,
      item: { id: bookmarkMenu.menuNumber },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      end: async ({ id: dragMenuNumber }, monitor) => {
        const didDrop = monitor.didDrop();
        if (!didDrop) {
          onMove(dragMenuNumber, bookmarkMenu.menuNumber);
        }

        onDrop();
      },
    }),
    [bookmarkMenu, onMove, onDrop]
  );

  const [, drop] = useDrop(
    () => ({
      accept: DND_ITEM_TYPE,
      hover({ id: dragMenuNumber }) {
        if (dragMenuNumber !== bookmarkMenu.menuNumber) {
          onMove(dragMenuNumber, bookmarkMenu.menuNumber);
        }
      },
    }),
    [onMove]
  );

  const handleBookmarkClick = async () => {
    await updateBookmarkMenu(!bookmarkMenu.isBookmark);
  };

  const handleMenuClick = () => {
    navigate(bookmarkMenu.menuUrl);
  };

  return (
    <Container
      ref={(node) => drag(drop(node))}
      active={pathname?.endsWith(bookmarkMenu.menuUrl) || pathname?.includes(`${bookmarkMenu.menuUrl}/`)}
      opacity={isDragging ? 0 : 1}
    >
      <Spacing left={'24px'} />
      <BookmarkIconWrapper onClick={handleBookmarkClick}>
        {bookmarkMenu.isBookmark ? <BookmarkSelectedIcon /> : <BookmarkIcon />}
      </BookmarkIconWrapper>
      <Spacing right={'6px'} />
      <LeafMenuName onClick={handleMenuClick}>{bookmarkMenu.menuName}</LeafMenuName>
      <BookmarkHandleWrapper className={'handle'}>
        <BookmarkHandle />
      </BookmarkHandleWrapper>
    </Container>
  );
};

LnbBookmarkMenu.propTypes = {
  bookmarkMenuNumber: PropTypes.string,
  onMove: PropTypes.func,
  onDrop: PropTypes.func,
};

export default LnbBookmarkMenu;
